import React, { useState } from 'react';
import { connect } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { selectIsWindowMode } from '@ff-os/pdp-shared-redux/slices/globalSlice';
import { Spaces } from '@ff-os/rendering-shared/coordinates';
import { IconButton } from '@ff-os/shared-react/components/buttons/icon-button/IconButton';
import { Cell } from '@ff-os/shared-react/components/grid/Cell';
import { Grid } from '@ff-os/shared-react/components/grid/Grid';
import { Icon } from '@ff-os/shared-react/components/icons/Icon';
import { useHas3d } from '@ff-os/shared-react/hooks/useHas3d';
import { useMobile } from '@ff-os/shared-react/hooks/useMobile';
import { useToggle } from '@ff-os/shared-react/hooks/useToggle';
import { ArPreviewStatus } from '@ff-scene-plugins/gift-redux/slices/uiSlice';
import { GiftPdpState } from '@ff-scene-plugins/gift-redux/store';
import { PrefabPreviewMode } from '@ff-scene-plugins/gift-shared/GiftSceneOptions';
import { selectMetaViewbox } from '../../../_redux-meta';
import { selectConfig3dOpenByDefault, selectHasConfig3d, selectShowArStatus, selectSpacesForFullPreview2d } from '../../../_redux-variants';
import { Controls3d } from '../../../controls3d/Controls3d';
import { DesignCredits } from '../../../preview/DesignCredits';
import { GiftPreview3d } from '../../../preview/GiftPreview3d';
import { createMinimalPreviewSpaces } from '../../../preview/helpers/createMinimalPreviewSpaces';
import { GiftWindowPreview2d } from './GiftWindowPreview2d';
import styles from '../../../GiftPreviewUi.module.scss';

const _GiftWindowPreviewUi = ({
    has3dConfig, open3dByDefault, availableSpaces,
    isWindowMode, canZoom, showArStatus,
    toggleFullscreen, isFullscreen, hideFullscreenButton
}: Props) => {

    const has3d = useHas3d(has3dConfig);
    const [show3d, set3d] = useState(has3d && open3dByDefault);
    const [zoom, toggleZoom] = useToggle(false);

    const spaces = availableSpaces[zoom ? 'minimal' : 'full'];
    const showZoomButton = useMobile() && canZoom && !show3d;
    const showButtons = useMobile() || isWindowMode;
    const showFullscreenButton = !hideFullscreenButton;

    return (
        <div className={styles.default}>
            {has3d && (
                <Controls3d
                    show3d={show3d}
                    set3d={set3d}
                    showArStatus={showArStatus} />)}

            {show3d
                ? <GiftPreview3d />
                : <GiftWindowPreview2d spaces={spaces} />}

            <DesignCredits />

            {showButtons && (showZoomButton || showFullscreenButton) && (
                <Grid direction='row' gap='s' className={styles.buttonGroup}>
                    {showZoomButton && (
                        <Cell>
                            <IconButton className={styles.button} small onClick={toggleZoom}>
                                <Icon type={zoom ? 'general-ui-zoom-out' : 'general-ui-zoom-in'} />
                            </IconButton>
                        </Cell>
                    )}

                    {showFullscreenButton && (
                        <Cell>
                            <IconButton className={styles.button} small onClick={toggleFullscreen}>
                                <Icon type={isFullscreen ? 'general-ui-minimize' : 'general-ui-maximize'} />
                            </IconButton>
                        </Cell>
                    )}
                </Grid>
            )}
        </div>
    );
};

type SpacesOptions = PrefabPreviewMode;
type Props = {

    has3dConfig: boolean;
    showArStatus: ArPreviewStatus;
    open3dByDefault: boolean;
    availableSpaces: Record<SpacesOptions, Spaces>;

    isFullscreen: boolean;
    toggleFullscreen: () => void;

    canZoom: boolean;
    isWindowMode: boolean;
    hideFullscreenButton?: boolean;
};

const mapStateToProps = (state: GiftPdpState) => ({
    has3dConfig: selectHasConfig3d(state),
    showArStatus: selectShowArStatus(state),
    open3dByDefault: selectConfig3dOpenByDefault(state),
    availableSpaces: selectAvailableSpaces(state),
    canZoom: selectZoomability(state),
    isWindowMode: selectIsWindowMode(state)
});

export const GiftWindowPreviewUi = connect(
    mapStateToProps
)(_GiftWindowPreviewUi);

const selectSpacesForMinimalPreview2d = createSelector([
    selectMetaViewbox
], createMinimalPreviewSpaces);

const selectAvailableSpaces = createSelector([
    selectSpacesForFullPreview2d,
    selectSpacesForMinimalPreview2d
], (fullSpaces, minimalSpaces) => ({
    'full': fullSpaces,
    'minimal': minimalSpaces
}));

const selectZoomability = createSelector([
    selectAvailableSpaces
], availableSpaces => availableSpaces.minimal.sceneWidth < availableSpaces.full.sceneWidth);